import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { UserPlus, X } from "lucide-react";

import { apiFetch, ApiError } from "@/lib/api";
import type { DocumentShareGrant, DocumentShareGranteeOption, DocumentShareScopeType } from "@/lib/documents";
import { useHubModuleAdmin } from "@/lib/useHubModuleAdmin";
import { Button } from "@/components/ui/Button";
import { SearchableSelect } from "@/components/ui/SearchableSelect";
import { ReadOnlyHoverLayer } from "@/components/ui/title-help";
import { cn } from "@/lib/utils";
import { DocumentModal } from "@/pages/documents/DocumentModal";

function grantSummary(grant: DocumentShareGrant): string {
  if (grant.scope_type === "sku" && grant.sku) {
    return `All documents on ${grant.sku}`;
  }
  return grant.document_title ?? "Document";
}

export function DocumentSharePanel(props: {
  documentRecordId?: string;
  sku?: string | null;
  /** When set with `sku`, user can share this document only or every document on the part number. */
  allowSkuBulkScope?: boolean;
  compact?: boolean;
}): JSX.Element | null {
  const queryClient = useQueryClient();
  const documentsAdmin = useHubModuleAdmin("documents");
  const [granteeSearch, setGranteeSearch] = useState("");
  const [selectedGranteeId, setSelectedGranteeId] = useState("");

  const canPickSkuBulk = Boolean(props.allowSkuBulkScope && props.sku?.trim());
  const [shareScope, setShareScope] = useState<DocumentShareScopeType>(() =>
    props.documentRecordId ? "document" : "sku",
  );

  const effectiveScope: DocumentShareScopeType =
    canPickSkuBulk && shareScope === "sku" ? "sku" : props.documentRecordId ? "document" : "sku";

  const grantsQuery = useQuery<{ grants: DocumentShareGrant[] }, ApiError>({
    queryKey: ["document-share-grants", props.documentRecordId, props.sku],
    queryFn: () =>
      apiFetch<{ grants: DocumentShareGrant[] }>("/api/document-share-grants", {
        query: {
          documentRecordId: props.documentRecordId || undefined,
          sku: props.sku?.trim() || undefined,
        },
      }),
    enabled:
      documentsAdmin &&
      (Boolean(props.documentRecordId) || Boolean(props.sku?.trim())),
  });

  const granteesQuery = useQuery<{ grantees: DocumentShareGranteeOption[] }, ApiError>({
    queryKey: ["document-share-grantees", granteeSearch],
    queryFn: () =>
      apiFetch<{ grantees: DocumentShareGranteeOption[] }>("/api/document-share-grantees", {
        query: { q: granteeSearch || undefined },
      }),
    enabled: documentsAdmin,
  });

  const createMutation = useMutation({
    mutationFn: (granteeUserId: string) =>
      apiFetch<{ grant: DocumentShareGrant | null }>("/api/document-share-grants", {
        method: "POST",
        body: {
          granteeUserId,
          scopeType: effectiveScope,
          sku: effectiveScope === "sku" ? props.sku?.trim() : undefined,
          documentRecordId: effectiveScope === "document" ? props.documentRecordId : undefined,
        },
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["document-share-grants"] });
      void queryClient.invalidateQueries({ queryKey: ["document-shared-sku-access"] });
      setSelectedGranteeId("");
      setGranteeSearch("");
    },
  });

  const revokeMutation = useMutation({
    mutationFn: (grantId: string) =>
      apiFetch(`/api/document-share-grants/${encodeURIComponent(grantId)}`, { method: "DELETE" }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["document-share-grants"] });
      void queryClient.invalidateQueries({ queryKey: ["document-shared-sku-access"] });
    },
  });

  const granteeOptions = useMemo(
    () =>
      (granteesQuery.data?.grantees ?? []).map((grantee) => ({
        value: grantee.userId,
        label: grantee.email ? `${grantee.label} (${grantee.email})` : grantee.label,
      })),
    [granteesQuery.data?.grantees],
  );

  const grants = grantsQuery.data?.grants ?? [];

  if (!documentsAdmin) {
    return null;
  }

  return (
    <div className={props.compact ? "space-y-3" : "rounded-lg border border-slate-200 bg-slate-50/80 p-4 space-y-3"}>
      {canPickSkuBulk ? (
        <fieldset className="space-y-2">
          <legend className="text-xs font-medium text-slate-600">Share scope</legend>
          <div className="flex flex-col gap-2 sm:flex-row">
            <label
              className={cn(
                "flex flex-1 cursor-pointer items-start gap-2 rounded-md border px-3 py-2 text-sm",
                shareScope === "document" ? "border-brand-500 bg-brand-50" : "border-slate-200 bg-white",
              )}
            >
              <input
                type="radio"
                name="document-share-scope"
                className="mt-1"
                checked={shareScope === "document"}
                onChange={() => setShareScope("document")}
              />
              <span>
                <span className="font-medium text-slate-900">This document only</span>
              </span>
            </label>
            <label
              className={cn(
                "flex flex-1 cursor-pointer items-start gap-2 rounded-md border px-3 py-2 text-sm",
                shareScope === "sku" ? "border-brand-500 bg-brand-50" : "border-slate-200 bg-white",
              )}
            >
              <input
                type="radio"
                name="document-share-scope"
                className="mt-1"
                checked={shareScope === "sku"}
                onChange={() => setShareScope("sku")}
              />
              <span>
                <span className="font-medium text-slate-900">All documents on part number</span>
                <span className="mt-0.5 block font-mono text-xs text-slate-600">{props.sku}</span>
              </span>
            </label>
          </div>
        </fieldset>
      ) : null}

      <div className="flex flex-col gap-2 sm:flex-row sm:items-end">
        <label className="block min-w-0 flex-1">
          <span className="block text-xs font-medium text-slate-600">Add person</span>
          <SearchableSelect
            className="mt-1"
            triggerClassName="h-9 text-sm"
            value={selectedGranteeId}
            onValueChange={(option) => setSelectedGranteeId(option?.value ?? "")}
            options={granteeOptions}
            searchValue={granteeSearch}
            onSearchChange={setGranteeSearch}
            placeholder="Choose hub user"
            searchPlaceholder="Search by name or email"
            emptyMessage="No matching users."
            isLoading={granteesQuery.isLoading}
          />
        </label>
        <ReadOnlyHoverLayer active={!selectedGranteeId} hoverHint="Choose a user first.">
          <Button
            type="button"
            variant="secondary"
            className="shrink-0"
            disabled={!selectedGranteeId || createMutation.isPending}
            isLoading={createMutation.isPending}
            onClick={() => {
              if (!selectedGranteeId) return;
              createMutation.mutate(selectedGranteeId);
            }}
          >
            <UserPlus className="mr-1.5 h-4 w-4" aria-hidden />
            Share
          </Button>
        </ReadOnlyHoverLayer>
      </div>

      {grantsQuery.isLoading ? (
        <p className="text-xs text-slate-500">Loading shares…</p>
      ) : grants.length === 0 ? (
        <p className="text-xs text-slate-500">No read-only shares yet.</p>
      ) : (
        <ul className="space-y-1">
          {grants.map((grant) => (
            <li
              key={grant.id}
              className="flex items-center justify-between gap-2 rounded-md border border-slate-200 bg-white px-2 py-1.5 text-sm"
            >
              <div className="min-w-0">
                <div className="truncate font-medium text-slate-800">
                  {grant.grantee_display_name ?? grant.grantee_email ?? grant.grantee_user_id}
                </div>
                <div className="truncate text-xs text-slate-500">{grantSummary(grant)}</div>
              </div>
              <button
                type="button"
                className="shrink-0 rounded-md p-1 text-slate-500 hover:bg-slate-100 hover:text-slate-800"
                aria-label={`Revoke share for ${grant.grantee_display_name ?? grant.grantee_email ?? "user"}`}
                disabled={revokeMutation.isPending}
                onClick={() => revokeMutation.mutate(grant.id)}
              >
                <X className="h-4 w-4" aria-hidden />
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export function DocumentShareModal(props: {
  open: boolean;
  sku: string;
  onClose: () => void;
}): JSX.Element | null {
  if (!props.open) {
    return null;
  }

  return (
    <DocumentModal
      title={`Share documents — ${props.sku}`}
      onClose={props.onClose}
      showCloseButton={false}
      shellClassName="max-w-lg"
      overlayClassName="z-[55] px-4 py-8"
    >
      <DocumentSharePanel compact sku={props.sku} />
      <div className="mt-4 flex justify-end gap-2">
        <Button type="button" variant="secondary" onClick={props.onClose}>
          Cancel
        </Button>
        <Button type="button" onClick={props.onClose}>
          Done
        </Button>
      </div>
    </DocumentModal>
  );
}
